import { get, has } from 'lodash'

import { isString } from './validators'

const componentsSchemasRefPrefix = '#/components/schemas/'

/**
 * Шаблон `pattern` из одного класса символов с квантификатором `+`: `^[...]+$`.
 *
 * @type {RegExp}
 */
const characterClassPatternRegExp = /^\^(\[.+\])\+\$$/

/**
 * Возвращает описание свойства схемы из `components.schemas` OpenAPI-документа.
 * Ссылка `$ref` на `#/components/schemas/<name>` раскрывается в описание целевой схемы.
 *
 * @param {object} openApiSchema OpenAPI-документ с разделом `components.schemas`.
 * @param {string} schemaName Имя схемы в `components.schemas`.
 * @param {string} propertyName Имя свойства в `properties` схемы.
 * @returns {object|undefined} Описание свойства или `undefined`, если свойство не найдено.
 */
export const getOpenApiSchemaProperty = (openApiSchema, schemaName, propertyName) => {
  const schemas = get(openApiSchema, 'components.schemas', {})
  const property = get(schemas, [ schemaName, 'properties', propertyName ])
  if (property && isString(property.$ref) && property.$ref.startsWith(componentsSchemasRefPrefix)) {
    return schemas[property.$ref.slice(componentsSchemasRefPrefix.length)]
  }

  return property
}

/**
 * Проверяет, что свойство перечислено в `required` схемы из `components.schemas` OpenAPI-документа.
 *
 * @param {object} openApiSchema OpenAPI-документ с разделом `components.schemas`.
 * @param {string} schemaName Имя схемы в `components.schemas`.
 * @param {string} propertyName Имя свойства в `properties` схемы.
 * @returns {boolean} Признак обязательного свойства.
 */
export const isOpenApiSchemaPropertyRequired = (openApiSchema, schemaName, propertyName) => {
  const required = get(openApiSchema, [ 'components', 'schemas', schemaName, 'required' ])

  return Array.isArray(required) && required.includes(propertyName)
}

/**
 * Возвращает значения `const` из вариантов `oneOf` описания свойства OpenAPI-схемы.
 * Варианты без `const` пропускаются.
 *
 * @param {object} property Описание свойства из `getOpenApiSchemaProperty`.
 * @returns {Array} Допустимые значения свойства в порядке вариантов `oneOf`.
 */
export const getOpenApiSchemaPropertyConstValues = (property = {}) => {
  return get(property, 'oneOf', []).filter(variant => {
    return has(variant, 'const')
  }).map(variant => {
    return variant.const
  })
}

/**
 * Возвращает опции маски ввода IMask для строкового свойства OpenAPI-схемы.
 * Маска строится только для `pattern` вида `^[...]+$`: ввод ограничивается этим
 * классом символов и длиной `maxLength`. Символ, не подходящий под класс, переводится
 * в верхний регистр, если в верхнем регистре он подходит.
 *
 * @param {object} property Описание свойства из `getOpenApiSchemaProperty`.
 * @returns {{ mask: RegExp, maskOptions: { prepareChar: Function } }|undefined} Опции маски
 * для `g-text-field` или `undefined`, если `pattern` другого вида.
 */
export const getInputMaskByOpenApiSchemaProperty = (property = {}) => {
  const match = isString(property.pattern) && property.pattern.match(characterClassPatternRegExp)
  if (!match) {
    return
  }

  const characterClass = match[1]
  let quantifier = '*'
  if (typeof property.maxLength === 'number') {
    quantifier = `{0,${property.maxLength}}`
  }
  const characterRegExp = new RegExp(`^${characterClass}$`)

  return {
    mask: new RegExp(`^${characterClass}${quantifier}$`),
    maskOptions: {
      prepareChar: value => {
        return [ ...value ].map(character => {
          const upperCaseCharacter = character.toUpperCase()
          if (!characterRegExp.test(character) && characterRegExp.test(upperCaseCharacter)) {
            return upperCaseCharacter
          }

          return character
        }).join('')
      }
    }
  }
}

/**
 * Проверяет длину строки по ограничениям `minLength` и `maxLength` описания свойства OpenAPI-схемы.
 * Отсутствующее ограничение не проверяется.
 *
 * @param {object} property Описание свойства из `getOpenApiSchemaProperty`.
 * @param {*} value Проверяемое значение.
 * @returns {boolean} `true`, если значение - строка допустимой длины.
 */
export const isValidLengthByOpenApiSchemaProperty = (property = {}, value) => {
  if (!isString(value)) {
    return false
  }
  if (typeof property.minLength === 'number' && value.length < property.minLength) {
    return false
  }
  if (typeof property.maxLength === 'number' && value.length > property.maxLength) {
    return false
  }

  return true
}

/**
 * Проверяет строку по ограничению `pattern` описания свойства OpenAPI-схемы.
 * Отсутствующее ограничение не проверяется.
 *
 * @param {object} property Описание свойства из `getOpenApiSchemaProperty`.
 * @param {*} value Проверяемое значение.
 * @returns {boolean} `true`, если значение - строка, подходящая под `pattern`.
 */
export const isValidPatternByOpenApiSchemaProperty = (property = {}, value) => {
  if (!isString(value)) {
    return false
  }
  if (isString(property.pattern)) {
    return new RegExp(property.pattern).test(value)
  }

  return true
}

/**
 * Проверяет строку по ограничениям `minLength`, `maxLength` и `pattern` описания свойства OpenAPI-схемы.
 * Отсутствующее ограничение не проверяется.
 *
 * @param {object} property Описание свойства из `getOpenApiSchemaProperty`.
 * @param {*} value Проверяемое значение.
 * @returns {boolean} `true`, если значение - строка, удовлетворяющая всем ограничениям.
 */
export const isValidByOpenApiSchemaProperty = (property = {}, value) => {
  return isValidLengthByOpenApiSchemaProperty(property, value) && isValidPatternByOpenApiSchemaProperty(property, value)
}
